import { useState } from "react"
import Button from "react-bootstrap/Button"
import { useLocation } from "react-router"
import "./index.scss"
import { ConfirmDialog } from "components"
import { Container, Text, Form } from "components/Form"
import { FiInfo } from "react-icons/fi"
import useT from "utils/hooks/useT"

// The main configuration container.
// Used to add a title and buttons at the bottom of a configuration.
// A lot of props can be passed for different scenarios. They are
// pretty self-explanatory
export default function ConfigContainer({
  title,
  titleInfoText,
  disabled,
  cancelHandler,
  canCancelWhenDisabled,
  submitHandler,
  deleteIsImportant,
  keyWordForAllowedDeletion,
  deleteHandler,
  shiftDeleteHandler,
  deleteTitle,
  // SPEC-045 (K3/K4/K11): when the delete/unplan action is blocked (e.g. a hard-locked analysis),
  // the caller passes deleteDisabled to render the delete button ghosted (grey, inactive, with a
  // not-allowed cursor + tooltip) instead of active — rather than hiding it. Backward-compatible:
  // both default off, so every other caller is unchanged.
  deleteDisabled = false,
  deleteDisabledTitle,
  confirmDeletionText,
  canDeleteWhenDisabled,
  saveTemplateHandler,
  saveAsHandler,
  saveDraftHandler,
  saveAsName,
  canSaveWhenDisabled,
  closeInsteadOfCancel,
  children,
  isDraft,
  isTemplate,
  className,
  editProjectHandler,
  // SPEC-066 (#4180): a READ-ONLY dialog — the body is wrapped in a disabled <fieldset>, which
  // natively deactivates every input, select, textarea and button inside it in one go, and the
  // footer shows only "Close" (no save / delete / save-as). Used when a view-only member opens an
  // unplanned project or standard action for details. Implies `disabled`.
  readOnly = false,
}) {
  const { t } = useT("common")
  const isDisabled = disabled || readOnly
  const showCancel = canCancelWhenDisabled || readOnly || !isDisabled
  const cancelReadsClose = closeInsteadOfCancel || readOnly
  const [activeConfirm, setActiveConfirm] = useState(false)
  const [allowDeletionKeyWord, setAllowDeletionKeyWord] = useState("")
  const [isWorking, setIsWorking] = useState(false)

  const shiftClickDeletionTexts = ["Are you sure you want to unplan all future actions in this project?"]
  const location = useLocation()
  const isAnalysisView = location.pathname.includes("/analyses/")

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault() // Prevent default form submission behavior

    if (readOnly) {
      // Nothing can have changed and there is no Save button; an Enter keypress must not submit.
      return
    }

    if (activeConfirm) {
      // If delete confirmation is active, do not proceed with submitHandler
      return
    }

    const returnValue = submitHandler()
    if (returnValue && title === "Analysis Definition") {
      setIsWorking(true)
      Promise.resolve(returnValue).finally(() => setIsWorking(false))
    }
  }

  // Handle deletion confirmation
  const handleConfirmDeletion = () => {
    if (deleteHandler) {
      deleteHandler(allowDeletionKeyWord)
    }
    setActiveConfirm(false)
    setAllowDeletionKeyWord("")
  }

  return (
    <Form
      className="ConfigContainer"
      onSubmit={handleSubmit}
    >
      {title && (
        <div className={titleInfoText ? "setupHeading ConfigContainer-Title" : "setupHeading"}>
          {title}
          {titleInfoText && (
            <div className="connection-info">
              <FiInfo title={titleInfoText} />
            </div>
          )}
        </div>
      )}
      <div className={`Config-main-container HiddenInput-container ${className}`}>
        {readOnly ? <fieldset disabled className="ConfigContainer-readonly">{children}</fieldset> : children}
        {deleteHandler && keyWordForAllowedDeletion && activeConfirm && (
          <ConfirmDialog
            onCancel={() => setActiveConfirm(false)}
            onConfirm={handleConfirmDeletion}
          >
            <h4>{t("toConfirmDeletionType", { keyword: keyWordForAllowedDeletion })}</h4>
            <Container>
              <Text
                value={allowDeletionKeyWord}
                onChange={(e) => setAllowDeletionKeyWord(e)}
                // Prevent Enter key from submitting the form
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation()
                  }
                }}
              />
            </Container>
          </ConfirmDialog>
        )}

        {deleteHandler && activeConfirm && !keyWordForAllowedDeletion && (
          <ConfirmDialog
            onCancel={() => setActiveConfirm(false)}
            onConfirm={() => {
              if (shiftDeleteHandler) {
                shiftDeleteHandler()
              } else {
                deleteHandler()
              }
              setActiveConfirm(false)
            }}
          >
            <h4>{confirmDeletionText || t("confirmDeletion")}</h4>
          </ConfirmDialog>
        )}
      </div>
      <div className="ConfigContainer-buttons">
        {(canDeleteWhenDisabled || !isDisabled) && deleteHandler && (
          deleteDisabled ? (
            // Ghosted: the wrapper span carries the not-allowed cursor + tooltip (a disabled
            // <button> has pointer-events:none and would show neither). SPEC-045 K11.
            <span className="blocked-action-wrap" title={deleteDisabledTitle}>
              <Button variant="secondary" className="blocked-action-btn" disabled>
                {deleteTitle || t("delete")}
              </Button>
            </span>
          ) : (
            <Button
              variant="danger"
              onClick={(e) => {
                const shiftKeyEffect = e.shiftKey && shiftClickDeletionTexts.includes(confirmDeletionText)
                if (deleteIsImportant || keyWordForAllowedDeletion || shiftKeyEffect) {
                  setActiveConfirm(true)
                } else {
                  deleteHandler()
                }
              }}
            >
              {deleteTitle || t("delete")}
            </Button>
          )
        )}
        {showCancel && (
          <Button
            variant="light"
            onClick={() => {
              cancelHandler()
            }}
          >
            {cancelReadsClose ? t("close") : t("cancel")}
          </Button>
        )}
        {editProjectHandler && !readOnly && (
          <Button variant="info" style={{color: "white"}} onClick={editProjectHandler}>
            {t("editProject")}
          </Button>
        )}
        {!isDisabled && saveTemplateHandler && (
          <Button variant="warning" onClick={() => saveTemplateHandler()}>
            {t("saveAsNewTemplate")}
          </Button>
        )}
        {!isDisabled && saveAsHandler && !isDraft &&  (
          <Button variant="warning" onClick={() => saveAsHandler()}>
            {saveAsName || t("saveAsNew")}
          </Button>
        )}
        {!isDisabled && isDraft && saveDraftHandler && (
          <Button variant="warning" onClick={() => saveDraftHandler()}>
            {t("saveAsDraft", { type: isAnalysisView ? "" : t("projectTemplate") })}
          </Button>
        )}
        {(canSaveWhenDisabled || !isDisabled) && !readOnly && (
          <Button variant="primary" type="submit" disabled={isWorking}>
            {isDraft ? t("saveAsType", { type: isTemplate ? t("projectTemplate") : t("analysisDefinition") }) : t("save")}
          </Button>
        )}
      </div>
    </Form>
  )
}

